import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Moon, Settings, Info } from "lucide-react";
import PrayerTimes from "@/components/PrayerTimes";
import NearbyMosques from "@/components/NearbyMosques";
import LocationPermission from "@/components/LocationPermission";
import NotificationScheduler from "@/components/NotificationScheduler";
import { useNotifications } from "@/hooks/use-notifications";
import mosqueHero from "@/assets/mosque-hero.jpg";

const Index = () => {
  const [hasLocationPermission, setHasLocationPermission] = useState<boolean | null>(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [prayerTimesData, setPrayerTimesData] = useState<any>(null);
  const [selectedMosque, setSelectedMosque] = useState<any>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute for real-time calculations
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Calculate time until Jummah prayer (only show on Friday and before prayer)
  const calculateTimeUntilJummah = () => {
    if (!prayerTimesData || !prayerTimesData.jummah) return null;
    
    const now = currentTime;
    const dayOfWeek = now.getDay();
    
    // Only show time until Jummah on Friday (day 5)
    if (dayOfWeek !== 5) return null;
    
    const today = now.toDateString();
    const jummahTime = new Date(`${today} ${prayerTimesData.jummah}`);
    
    // If today's Jummah has passed, don't show countdown
    if (jummahTime.getTime() < now.getTime()) {
      return null;
    }
    
    // Today's Jummah hasn't passed yet - show countdown
    const timeUntil = (jummahTime.getTime() - now.getTime()) / (1000 * 60 * 60); // Convert to hours
    return Math.max(timeUntil, 0);
  };
  const { 
    hasPermission, 
    isRequesting, 
    settings, 
    requestPermission, 
    updateSettings 
  } = useNotifications();

  const handleMosqueSelected = (mosque: any | null) => {
    setSelectedMosque(mosque);
    try {
      if (mosque) {
        localStorage.setItem('jummah-selected-mosque', JSON.stringify(mosque));
      } else {
        localStorage.removeItem('jummah-selected-mosque');
      }
    } catch {}
  };

  // Check location permission on mount
  useEffect(() => {
    const checkLocationPermission = () => {
      if (!navigator.geolocation) {
        setHasLocationPermission(false);
        return;
      }

      // Try to get position to check if permission was previously granted
      navigator.geolocation.getCurrentPosition(
        () => {
          setHasLocationPermission(true);
        },
        (error) => {
          // If permission was denied or not granted, show permission screen
          setHasLocationPermission(false);
        },
        { timeout: 1000, maximumAge: 300000 } // 5 minute cache
      );
    };

    checkLocationPermission();
  }, []);

  // Load saved selected mosque
  useEffect(() => {
    try {
      const saved = localStorage.getItem('jummah-selected-mosque');
      if (saved) {
        setSelectedMosque(JSON.parse(saved));
      }
    } catch {}
  }, []);

  // Show loading while checking permission
  if (hasLocationPermission === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking location permissions...</p>
        </div>
      </div>
    );
  }

  if (!hasLocationPermission) {
    return <LocationPermission onPermissionGranted={() => setHasLocationPermission(true)} />;
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${mosqueHero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/40 to-background/95"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-16">
          {/* Navigation */}
          <nav className="flex justify-between items-center mb-12">
            <div className="flex items-center gap-2 text-white">
              <Moon className="h-6 w-6" />
              <span className="font-semibold">Jummah Reminder</span>
            </div>
            <Link 
              to="/about" 
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <Info className="h-4 w-4" />
              About
            </Link>
          </nav>

          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Moon className="h-8 w-8 text-gold" />
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Jummah Reminder
                </h1>
              </div>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Never miss Friday prayers. Get timely reminders and find nearby mosques with precise travel times.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Button 
                variant="golden" 
                size="lg"
                onClick={requestPermission}
                disabled={isRequesting}
              >
                <Bell className="h-5 w-5 mr-2" />
                {isRequesting ? "Requesting..." : hasPermission ? "Notifications Enabled" : "Enable Notifications"}
              </Button>
              <Button variant="mosque" size="lg" onClick={() => setShowNotificationSettings(!showNotificationSettings)}>
                <Settings className="h-5 w-5 mr-2" />
                Settings
              </Button>
            </div>

            {/* Notification Settings Panel */}
            {showNotificationSettings && (
              <Card className="p-6 mb-8 bg-white/95 backdrop-blur-sm">
                <h3 className="text-lg font-semibold text-primary mb-4">Notification Preferences</h3>
                {!hasPermission && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">Enable notifications first to configure these settings.</p>
                  </div>
                )}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium">Morning Reminder</p>
                      <p className="text-sm text-muted-foreground">Get mosque suggestions a few hours before Jummah</p>
                    </div>
                    <Switch 
                      checked={settings.morningReminder}
                      onCheckedChange={(checked) => updateSettings({ morningReminder: checked })}
                      disabled={!hasPermission}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium">Pre-Prayer Alert</p>
                      <p className="text-sm text-muted-foreground">Alert when it's time to leave for the mosque</p>
                    </div>
                    <Switch 
                      checked={settings.prePrayerAlert}
                      onCheckedChange={(checked) => updateSettings({ prePrayerAlert: checked })}
                      disabled={!hasPermission}
                    />
                  </div>
                  
                  {settings.prePrayerAlert && (
                    <div className="pl-4 border-l-2 border-primary/20">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">Reminder Time</p>
                          <p className="text-xs text-muted-foreground">How early to notify before Jummah</p>
                        </div>
                        <Select 
                          value={settings.reminderTime.toString()} 
                          onValueChange={(value) => updateSettings({ reminderTime: parseInt(value) })}
                          disabled={!hasPermission}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="45">45 minutes</SelectItem>
                            <SelectItem value="60">1 hour</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Prayer Times Section */}
          <section>
            <PrayerTimes onPrayerTimesLoaded={setPrayerTimesData} />
          </section>

          {/* Nearby Mosques Section */}
          <section>
            <NearbyMosques timeUntilJummah={calculateTimeUntilJummah()} onMosqueSelected={handleMosqueSelected} />
          </section>

          {/* Notification Scheduler - handles automatic prayer reminders */}
          <NotificationScheduler 
            prayerTimes={prayerTimesData} 
            selectedMosque={selectedMosque} 
          />

          {/* Information Cards */}
          <section className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10">
              <h3 className="text-lg font-semibold text-primary mb-3">How It Works</h3>
              <div className="space-y-3 text-sm">
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <p>Get notified Friday morning with nearby mosque options</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <p>Receive travel time calculations for walking and driving</p>
                </div>
                <div className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <p>Get reminded when it's time to leave based on your preferred transport</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-accent/5 to-accent/10">
              <h3 className="text-lg font-semibold text-accent mb-3">Privacy & Security</h3>
              <div className="space-y-3 text-sm">
                <p>• Your location is only used to find nearby mosques</p>
                <p>• No personal data is stored on our servers</p>
                <p>• All calculations happen locally on your device</p>
                <p>• You can disable notifications anytime</p>
              </div>
            </Card>
          </section>

          {/* Footer */}
          <footer className="text-center py-8 border-t border-border">
            <p className="text-muted-foreground mb-3">
              May Allah make it easy for you to attend Jummah prayers regularly. Ameen.
            </p>
            <p className="text-sm text-muted-foreground/70">
              © {new Date().getFullYear()} Jummah Reminder. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Index;